import { CancellationToken } from '../../hooks/useEffectAsync';
import useStateRef from '../../hooks/useStateRef';
import { useCallback, useEffect, useState } from 'react';
import { SessionResponse } from '../../models/ChatTypes';
import { useChat } from '../chat/ChatContext';
import { SessionEvent, SessionEventTypesPrefixes } from '../../models/BackendEvents';
import { GLOBAL_MODAL_ERROR_ID, useErrorHandlerContext } from '../error-handler/ErrorContext';
import { HttpError } from '../../services/HTTPDataService';
import useLabels from '../../hooks/useLabels';


const useChatsHistory = () => {
    const { getAllChats, events: { session: sessionEvents } } = useChat();
    const [chatsHistory, setChatsHistory, chatsHistoryRef] = useStateRef<SessionResponse[]>();
    const [isLoading, setIsLoading] = useState(false);
    const { registerError, removeError } = useErrorHandlerContext();
    const labels = useLabels();

    const loadChatsHistory = useCallback((async ({ token, force }: { token?: CancellationToken, force?: boolean; }) => {
        if (chatsHistory?.length && !force) return;

        setIsLoading(true);
        removeError(GLOBAL_MODAL_ERROR_ID);
        try {
            if (token?.cancelled) return;
            const chats = await getAllChats();

            setChatsHistory([...chats]);
        } catch (e) {
            if (token?.cancelled) return;
            console.error({ e });

            // 401/403 are already redirected to login globally via HTTPDataService.triggerUnauthorized
            const status = (e as HttpError)?.status;
            if (status !== 401 && status !== 403) {
                registerError({
                    [GLOBAL_MODAL_ERROR_ID]: {
                        type: 'modal',
                        headline: labels.chatsHistoryLoadErrorHeadline,
                        notification: { headline: labels.chatsHistoryLoadErrorDescription, details: e as any }
                    }
                });
            }
        } finally {
            setIsLoading(false);
        }
    }), [chatsHistory, getAllChats, setChatsHistory, registerError, removeError, labels.chatsHistoryLoadErrorHeadline, labels.chatsHistoryLoadErrorDescription]);


    const reloadChatsHistory = useCallback(async (force?: boolean) => {
        // only reload force is true or chat history has not been loaded
        if (force || !chatsHistoryRef.current)
            await loadChatsHistory({ force });
        return Promise.resolve<void>(undefined);
    }, [chatsHistoryRef, loadChatsHistory]);

    const onSessionEvent = useCallback((model: SessionEvent) => {
        // Handling workflow updates
        // TODO: handle name updates.
        if (!model.type.startsWith(SessionEventTypesPrefixes.workflow)) {
            return;
        }
        const targetIndex = chatsHistoryRef.current?.findIndex(x => x.session_id === model.session_id) ?? -1;

        if (targetIndex === -1) {
            return;
        }
        const target = chatsHistoryRef.current![targetIndex];
        const copy = [...chatsHistoryRef.current!];
        copy[targetIndex] = { ...target, workflow_status: model.type.replace(SessionEventTypesPrefixes.workflow, "") as any };
        setChatsHistory(copy);
    }, [chatsHistoryRef, setChatsHistory]);

    useEffect(() => {
        const events = sessionEvents;
        events.on(onSessionEvent);
        return () => events.off(onSessionEvent);
    }, [sessionEvents, onSessionEvent]);

    return {
        chatHistory: {
            items: chatsHistory,
            set: setChatsHistory,
            reload: reloadChatsHistory,
            isLoading
        }
    };
};

export default useChatsHistory;